<template>
  <div class="o-toolbar">
    <div class="o-toolbar__left">
      <h2 class="c-heading-2">{{ klas.title }}</h2>
    </div>
  </div>
  <form @submit.prevent="onSubmit">
    <div class="o-form">
      <!-- Parent -->
      <div class="o-input">
        <label class="o-input__label">Ouder</label>
        <div class="o-input__controls">
          <div class="o-grid">
            <!-- Parent Voornaam -->
            <div class="o-grid__col">
              <div class="o-input o-input--vertical">
                <label for="parentGivenName" class="o-input__label">Voornaam</label>
                <div class="o-input__controls">
                  <input
                    type="text"
                    class="c-textfield"
                    id="parentGivenName"
                    autocomplete="name given-name"
                    :required="!parentIsOptional"
                    v-model="contact.parent.givenName"
                    placeholder="Voornaam Ouder"
                    @change="updateContact"
                  />
                </div>
              </div>
            </div>
            <!-- Parent Achternaam -->
            <div class="o-grid__col">
              <div class="o-input o-input--vertical">
                <label for="parentFamilyName" class="o-input__label">Achternaam</label>
                <div class="o-input__controls">
                  <input
                    type="text"
                    class="c-textfield"
                    id="parentFamilyName"
                    autocomplete="name family-name"
                    required
                    v-model="contact.parent.familyName"
                    placeholder="Achternaam Ouder"
                    @change="updateContact"
                  />
                </div>
              </div>
            </div>
            <!-- Parent Email -->
            <div class="o-grid__col">
              <div class="o-input o-input--vertical">
                <label for="parentEmail" class="o-input__label">E-mail</label>
                <div class="o-input__controls">
                  <input
                    type="email"
                    class="c-textfield"
                    id="parentEmail"
                    autocomplete="email"
                    required
                    v-model="contact.parent.email"
                    placeholder="E-mail Ouder"
                    @change="updateContact"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Student -->
      <div class="o-input">
        <label class="o-input__label">Leerling</label>
        <div class="o-input__controls">
          <div v-if="contact.student.number != null" class="o-grid">
            <div class="o-grid__col">
              <div class="o-input o-input--vertical">
                <label for="studentNummer" class="o-input__label">Nummer</label>
                <div class="o-input__controls">
                  <input type="text" class="c-textfield" id="studentNummer" readonly :value="contact.student.number" />
                </div>
              </div>
            </div>
          </div>
          <div class="o-grid">
            <!-- Student Voornaam -->
            <div class="o-grid__col">
              <div class="o-input o-input--vertical">
                <label for="studentGivenName" class="o-input__label">Voornaam</label>
                <div class="o-input__controls">
                  <input
                    type="text"
                    class="c-textfield"
                    id="studentGivenName"
                    autocomplete="name given-name"
                    required
                    v-model="contact.student.givenName"
                    placeholder="Voornaam Leerling"
                    @change="updateContact"
                  />
                </div>
              </div>
            </div>
            <!-- Student Achternaam -->
            <div class="o-grid__col">
              <div class="o-input o-input--vertical">
                <label for="studentFamilyName" class="o-input__label">Achternaam</label>
                <div class="o-input__controls">
                  <input
                    type="text"
                    class="c-textfield"
                    id="studentFamilyName"
                    autocomplete="name family-name"
                    required
                    v-model="contact.student.familyName"
                    placeholder="Achternaam Leerling"
                    @change="updateContact"
                  />
                </div>
              </div>
            </div>
            <!-- Student Email -->
            <div class="o-grid__col">
              <div class="o-input o-input--vertical">
                <label for="studentEmail" class="o-input__label">E-mail</label>
                <div class="o-input__controls">
                  <input
                    type="email"
                    class="c-textfield"
                    id="studentEmail"
                    autocomplete="email"
                    required
                    v-model="contact.student.email"
                    placeholder="E-mail Leerling"
                    @change="updateContact"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="o-input">
        <label for="comments" class="o-input__label">Opmerkingen</label>
        <div class="o-input__controls">
          <div class="o-input__controls"><textarea id="comments" v-model="contact.comments" class="c-textfield" @change="updateContact"></textarea></div>
        </div>
      </div>
      <div class="o-input">
        <label class="o-input__label">Te betalen</label>
        <div class="o-input__controls">{{ formatCurrency(totalCost) }} euro</div>
      </div>
    </div>
    <footer class="c-container__footer">
      <div class="o-toolbar">
        <div class="o-toolbar__left">
          <button type="button" class="c-button c-button--default" @click="goBack">Terug</button>
        </div>
        <div class="o-toolbar__right">
          <button type="submit" class="c-button c-button--primary" :class="isSubmitting && 'c-button--disabled'" :disabled="isSubmitting">
            {{ isSubmitting ? "Bestelling wordt bevestigd, even geduld" : skipPayment ? "Bevestigen" : "Betalen" }}
          </button>
        </div>
      </div>
      <p style="float: right" v-if="hasError" class="has-warning">Er liep helaas iets mis met het verzenden van de bestelling.</p>
    </footer>
  </form>
</template>

<script>
import { formatCurrency } from "@/infrastructure/utilities";

import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { ref, reactive, computed, onMounted } from "vue";

export default {
  props: {
    contactData: {
      type: Object,
      default: () => ({
        parent: { givenName: "", familyName: "", email: "" },
        student: { givenName: "", familyName: "", email: "" },
        comments: "",
      }),
    },
  },
  setup(props) {
    const router = useRouter();
    const store = useStore();

    const skipPayment = computed(() => store.state.webshop.skipPayment);
    const parentIsOptional = computed(() => store.state.webshop.parentIsOptional);
    const isSubmitting = ref(false);
    const hasError = ref(false);

    const klas = computed(() => store.getters["data/findKlas"](store.state.orders.order.klasId));
    const contact = reactive(props.contactData);
    onMounted(() => {
      const contactData = store.getters["orders/contact"];
      contact.parent = { ...contactData.parent };
      contact.student = { ...contactData.student };
      contact.comments = contactData.comments;
    });

    const updateContact = () => {
      store.commit("orders/setContact", contact);
    };

    const totalCost = computed(() => store.getters["orders/totalCostWithDiscount"]);

    const goBack = () => router.push({ name: "besteloverzicht" });
    const onSubmit = async () => {
      hasError.value = false;
      isSubmitting.value = true;
      try {
        await store.dispatch("orders/submitContact", contact);
        await store.dispatch("orders/save");
      } catch (ex) {
        hasError.value = true;
        console.error("Failed to initiate payment", { error: ex });
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      skipPayment,
      parentIsOptional,
      isSubmitting,
      hasError,

      klas,
      contact,

      totalCost,
      formatCurrency,

      updateContact,
      goBack,
      onSubmit,
    };
  },
};
</script>
